import React, { useState, useEffect, useContext } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import AppContext from "../../../context/AppContext";
import {
  LayoutDashboard,
  BookMarked,
  FileText,
  StickyNote,
  Layers,
  Scale,
  Star,
  Bell,
  User,
  Bot,
  LogOut,
  ChevronLeft,
  ChevronRight,
  X,
  Menu,
} from "lucide-react";
import "../../../assets/css/gcstudent/studentsidebar/StudentSideBar.css";
import Swal from "sweetalert2";

/* ── Nav Items ── */
const navItems = [
  { label: "Dashboard",     icon: <LayoutDashboard size={17} />, path: "/student/dashboard" },
  { label: "My Books",      icon: <BookMarked size={17} />,      path: "/student/my-books" },
  { label: "Saved PDFs",    icon: <FileText size={17} />,        path: "/student/saved-pdfs" },
  { label: "Saved Notes",   icon: <StickyNote size={17} />,      path: "/student/saved-notes" },
  { label: "Flash Cards",   icon: <Layers size={17} />,          path: "/student/flash-cards" },
  { label: "Disputes",      icon: <Scale size={17} />,           path: "/student/disputes" },
  { label: "Notifications", icon: <Bell size={17} />,            path: "/student/notifications" },
  { label: "Write Review",  icon: <Star size={17} />,            path: "/student/review" },
  { label: "Profile",       icon: <User size={17} />,            path: "/student/profile" },
  { label: "SmartLib AI",       icon: <Bot size={17} />,             path: "/student/libraro" },
];

const MOBILE_BP = 992;

const StudentSideBar = ({ onCollapsedChange, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setAuthToken, setLoggedInUser } = useContext(AppContext);

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BP);

  useEffect(() => {
    const fn = () => {
      const mobile = window.innerWidth < MOBILE_BP;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname]);

  const handleDesktopToggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    if (onCollapsedChange) onCollapsedChange(next);
  };

   const handleLogout = () => {
      Swal.fire({
        title: "Logout?",
        text: "Are you sure you want to logout from Student Panel?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Logout",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#d63031",
        cancelButtonColor: "#1a5f7a",
      }).then((result) => {
        if (result.isConfirmed) {
          // Sab kuch clear karo
          localStorage.removeItem("authToken");
          localStorage.removeItem("userType");
          localStorage.removeItem("Student");
          localStorage.removeItem("StudentEmail");
          localStorage.removeItem("StudentPassword");
  
          // Context bhi reset karo
          if (setAuthToken)    setAuthToken(null);
          if (setLoggedInUser) setLoggedInUser(null);
  
          navigate("/");
        }
      });
    };

  const sidebarClass = [
    "gcstudent-sidebar",
    isMobile ? "gcstudent-sidebar--mobile" : "",
    isMobile && mobileOpen ? "gcstudent-sidebar--mobile-open" : "",
    !isMobile && collapsed  ? "gcstudent-sidebar--collapsed"  : "",
  ].filter(Boolean).join(" ");

  return (
    <>
      {/* Mobile Top Bar */}
      {isMobile && (
        <div className="gcstudent-topbar">
          <button
            className="gcstudent-topbar__hamburger"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Open menu"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={20} />}
          </button>
          <span className="gcstudent-topbar__brand">Student Panel</span>
        </div>
      )}

      {/* Mobile Backdrop */}
      {isMobile && mobileOpen && (
        <div
          className="gcstudent-sidebar__backdrop"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar Panel */}
      <aside className={sidebarClass}>

        {/* Header */}
        <div className="gcstudent-sidebar__head">
          {isMobile ? (
            <>
              <span className="gcstudent-sidebar__brand">Student Portal</span>
              <button
                className="gcstudent-sidebar__toggle"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
              >
                <X size={16} />
              </button>
            </>
          ) : (
            <>
              {!collapsed && <span className="gcstudent-sidebar__brand">Student Portal</span>}
              <button
                className="gcstudent-sidebar__toggle"
                onClick={handleDesktopToggle}
                aria-label={collapsed ? "Expand" : "Collapse"}
              >
                {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
              </button>
            </>
          )}
        </div>

        {/* Nav */}
        <nav className="gcstudent-sidebar__nav">
          {navItems.map((item) => {
            const active =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + "/");
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`gcstudent-sidebar__link ${active ? "gcstudent-sidebar__link--active" : ""}`}
                title={collapsed && !isMobile ? item.label : ""}
              >
                <span className="gcstudent-sidebar__icon">{item.icon}</span>
                <span className="gcstudent-sidebar__label">{item.label}</span>
                {active && !collapsed && <span className="gcstudent-sidebar__dot" />}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer: Log Out */}
        <div className="gcstudent-sidebar__footer">
          <div className="gcstudent-sidebar__divider" />
          <button
            className="gcstudent-sidebar__logout"
            onClick={handleLogout}
            title={collapsed && !isMobile ? "Log Out" : ""}
          >
            <span className="gcstudent-sidebar__icon gcstudent-sidebar__icon--logout">
              <LogOut size={17} />
            </span>
            <span className="gcstudent-sidebar__label">Log Out</span>
          </button>
        </div>

      </aside>
    </>
  );
};

export default StudentSideBar;